import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { Modal } from '@/components/Modal';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { MailAttachmentCard } from '@/pages/Mail/components/MailAttachmentCard';
import { VideoAttendeeRow } from '@/pages/Mail/components/VideoAttendeeRow';
import { VideoCard } from '@/pages/Mail/components/VideoCard';
import { useVideoMeeting } from '@/pages/Mail/hooks/useVideoMeeting';
import type { MailAttachment } from '@/types/mail';
import type { VideoMeeting } from '@/types/video';

const TITLE_SWAP_PROGRESS = 0.5;

interface Props {
  meeting: VideoMeeting;
  isMobile: boolean;
  isResponding?: boolean;
  onClose: () => void;
  onJoin: (id: string) => void;
  onApprove: (id: string) => void;
  onDecline: (id: string) => void;
  onOpenAttachment: (attachment: MailAttachment) => void;
}

export const VideoDetailModal: React.FC<Props> = ({
  meeting,
  isMobile,
  isResponding,
  onClose,
  onJoin,
  onApprove,
  onDecline,
  onOpenAttachment,
}) => {
  const { t } = useTranslation();
  const { progress, handleScroll } = useScrollFadeProgress();

  const { data: meetingDetails, isLoading } = useVideoMeeting(meeting.id);

  const detailedMeeting = meetingDetails ?? meeting;
  const { attendees, attachments } = detailedMeeting;
  const hasAttachments = attachments.length > 0;
  const hasAttendees = attendees.length > 0;
  const isTitleInHeader = progress > TITLE_SWAP_PROGRESS;
  const title = t(
    isMobile ? 'mail.video.detail.mobileTitle' : 'mail.video.detail.title',
  );

  const content = (
    <div className="flex min-h-full flex-col px-4 pt-2 pb-4 md:min-h-0 md:px-5 md:pt-2 md:pb-5">
      <h2
        className={`text-title-36 mb-4 text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-0' : 'opacity-100'}`}
      >
        {title}
      </h2>

      <VideoCard
        isSummary
        meeting={detailedMeeting}
        isMobile={isMobile}
        isResponding={isResponding}
        onJoin={onJoin}
        onApprove={onApprove}
        onDecline={onDecline}
      />

      {hasAttachments && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl bg-card p-4 shadow-card">
          <p className="text-subhead-14 text-subtle-foreground">
            {t('mail.video.detail.attachment')}
          </p>
          <div className="flex flex-wrap gap-6">
            {attachments.map((attachment) => (
              <MailAttachmentCard
                key={attachment.id}
                attachment={attachment}
                onPreview={onOpenAttachment}
              />
            ))}
          </div>
        </div>
      )}

      {hasAttendees && (
        <div className="mt-8 flex flex-col gap-6">
          <p className="text-subhead-14 text-muted-foreground">
            {t('mail.video.detail.invitedPeople')}
          </p>
          <div className="flex flex-col gap-4">
            {attendees.map((attendee, index) => (
              <VideoAttendeeRow
                key={attendee.id}
                attendee={attendee}
                index={index}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen
      label={title}
      className="md:h-[788px] md:max-h-[calc(100svh-80px)] md:w-[min(853px,calc(100vw-40px))]"
      onClose={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="relative flex shrink-0 items-center gap-3 px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
          <button
            type="button"
            aria-label={t('global.back')}
            onClick={onClose}
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
          >
            <ArrowLeft />
          </button>

          <h2
            className={`text-callout-16 pointer-events-none absolute left-1/2 -translate-x-1/2 text-foreground transition-opacity duration-75 md:hidden ${isTitleInHeader ? 'opacity-100' : 'opacity-0'}`}
          >
            {title}
          </h2>

          <h2 className="text-headline-20 hidden flex-1 text-foreground md:block">
            {title}
          </h2>

          <button
            type="button"
            aria-label={t('global.close')}
            onClick={onClose}
            className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
          >
            <Close />
          </button>
        </header>

        {isLoading && (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <PageSpinner className="size-[22px] text-subtle-foreground" />
          </div>
        )}

        {!isLoading && isMobile && (
          <div
            data-modal-scroll
            onScroll={handleScroll}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
          >
            {content}
          </div>
        )}

        {!isLoading && !isMobile && (
          <AutoHideScrollArea
            className="flex min-h-0 flex-1 flex-col"
            viewportClassName="[&>div]:h-full"
            onScroll={handleScroll}
          >
            {content}
          </AutoHideScrollArea>
        )}
      </div>
    </Modal>
  );
};
